// PendingAssignmentBanner — top-of-page warning for platform users (Phase A2).
//
// Shown when the logged-in user is a platform user (account_manager / operator
// / supervisor) with ZERO entries in platform_user_assignments. They CAN sign
// in, but they have no practice context, so most surfaces will be empty until
// a platform_admin attaches them via the Platform Team → Assignments panel.
//
// Suppressed for: practice users, platform_admin (global by definition),
// and any non-admin platform user with ≥1 assignment.
//
// Data source: /auth/me response includes `assignment_count` (Phase A2). The
// react-query cache keyed ['current-user'] already holds it; we don't fire a
// separate request.

import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { useCurrentUser } from '../../hooks';

export default function PendingAssignmentBanner() {
  const { data } = useCurrentUser();
  const u = data as
    | (typeof data & { user_type?: string; role?: string; assignment_count?: number | null })
    | undefined;

  if (!u) return null;
  if (u.user_type !== 'platform_user') return null;
  if (u.role === 'platform_admin') return null;
  // assignment_count is a number for non-admin platform users; null otherwise.
  if (u.assignment_count !== 0) return null;

  return (
    <div
      role="status"
      className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-start gap-2"
    >
      <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
      <span className="text-sm text-amber-900 leading-snug">
        <strong>Pending assignment.</strong> Your account is created but not yet attached to any
        practice — you'll have limited access until your administrator assigns you in <em>Platform
        Team → Assignments</em>.
      </span>
    </div>
  );
}
